import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { ManageBehaviorStrategiesService } from '../manage-behavior-strategies.service';
import { AuditContextService } from '../../../../../../features/audit-logs/audit-context.service';
import {
  BehaviorSubject,
  filter,
  finalize,
  Observable,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import { ErrorResponse } from '../../../../../../core/models/error.models';
import { CommonModule } from '@angular/common';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import {
  BehaviorStrategyDetailPromptAnswerPayload,
  CreateOwnStrategyPayload,
  StrategyItem,
  StrategyQueryParams,
} from '../strategy.interface';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { SvgIconComponent } from 'angular-svg-icon';
import { Router } from '@angular/router';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ManageStudentDrawerParams } from '../../../../insights.interface';
import { StudentInfoCardComponent } from '../../../../../../components/student-info-card/student-info-card.component';
import { BehaviorInfoCardComponent } from '../../../../../../components/behavior-info-card/behavior-info-card.component';

interface ManageOwnStrategyParams {
  strategy: StrategyItem;
  mode: 'view' | 'edit' | 'create' | 'guide';
  behaviorId?: number;
  strategyTypeId?: number;
  from?: 'Guide me' | 'Create my own';
  caseParams: ManageStudentDrawerParams;
}

@Component({
  selector: 'app-create-own-strategy',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NzInputModule,
    NzFormModule,
    NzButtonModule,
    NzModalModule,
    SvgIconComponent,
    NzSpinModule,
    StudentInfoCardComponent,
    BehaviorInfoCardComponent,
  ],
  templateUrl: './create-own-strategy.component.html',
  styleUrl: './create-own-strategy.component.scss',
})
export class CreateOwnStrategyComponent implements OnDestroy {
  @Input() caseBehaviorId!: number;
  @Input() type!: number;
  @Output() cancelled = new EventEmitter<void>();

  private readonly router = inject(Router);

  strategy?: StrategyItem;
  mode: 'view' | 'edit' | 'create' | 'guide' = 'create';
  from!: 'Guide me' | 'Create my own' | '';
  behaviorId?: number;
  isPublished = false;
  strategyTypeId?: number;
  caseParams!: ManageStudentDrawerParams;

  loading$: Observable<boolean> = of(false);

  showDeleteConfirmModal = false;
  guidelineToDeleteIndex: number | null = null;

  deletedGuidelines = new Set<number>();

  createOwnStrategyForm: FormGroup;

  private readonly loaderService = inject(LoaderService);
  private readonly commonService = inject(CommonService);
  private readonly drawerService = inject(DrawerService);
  private readonly strategyService = inject(ManageBehaviorStrategiesService);
  private readonly auditContextService = inject(AuditContextService);
  private readonly destroy$ = new Subject<void>();
  private readonly fb = inject(FormBuilder);

  private readonly paramsSubject =
    new BehaviorSubject<StrategyQueryParams | null>(null);

  readonly defaultParams$ = this.paramsSubject.asObservable();

  readonly MAX_LENGTHS = {
    title: 200,
    description: 4000,
    details: 2000,
    guideline: 500,
    detailPromptAnswer: 2000,
  };

  readonly ERROR_MESSAGES = {
    title: {
      required: 'Title is required',
      maxlength: `Title cannot exceed ${this.MAX_LENGTHS.title} characters`,
      pattern: 'Title can only contain letters and spaces',
    },
    description: {
      required: 'Description is required',
      maxlength: `Description cannot exceed ${this.MAX_LENGTHS.description} characters`,
    },
    details: {
      required: 'Details are required',
      maxlength: `Details cannot exceed ${this.MAX_LENGTHS.details} characters`,
    },
    guideline: {
      required: 'Guideline is required',
    },
    detailPromptAnswer: {
      required: 'Answer is required',
      maxlength: `Answer cannot exceed ${this.MAX_LENGTHS.detailPromptAnswer} characters`,
    },
  };

  constructor() {
    this.createOwnStrategyForm = this.fb.group({
      title: [
        '',
        [Validators.required, Validators.maxLength(this.MAX_LENGTHS.title)],
      ],
      description: [
        '',
        [
          Validators.required,
          Validators.maxLength(this.MAX_LENGTHS.description),
        ],
      ],
      guidelines: this.fb.array([this.createDefaultGuideline()]),
      detailPromptAnswers: this.fb.array([]),
      details: [
        '',
        [Validators.required, Validators.maxLength(this.MAX_LENGTHS.details)],
      ],
    });
    this.setupDrawerSubscription();
    this.setupFormValidation();
    this.loading$ = this.loaderService.loading$;
  }

  private setupFormValidation(): void {
    this.createOwnStrategyForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.validateForm();
      });
  }

  private validateForm(): void {
    Object.keys(this.createOwnStrategyForm.controls).forEach(key => {
      const control = this.createOwnStrategyForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  getErrorMessage(controlName: string): string {
    const control = this.createOwnStrategyForm.get(controlName);
    if (!control?.errors) return '';

    const errors =
      CreateOwnStrategyComponent.prototype.ERROR_MESSAGES[
        controlName as keyof typeof CreateOwnStrategyComponent.prototype.ERROR_MESSAGES
      ];
    const firstError = Object.keys(control.errors)[0];
    return errors[firstError as keyof typeof errors] || 'Invalid input';
  }

  get guidelinesArray() {
    return this.createOwnStrategyForm?.get('guidelines') as FormArray;
  }

  get detailPromptAnswersArray() {
    return this.createOwnStrategyForm?.get('detailPromptAnswers') as FormArray;
  }

  createGuideline(guideline?: string): FormGroup {
    return this.fb.group({
      guideline: [guideline?.trim() || '', [Validators.required]],
      sequenceNumber: [this.guidelinesArray?.controls?.length + 1 || 1],
    });
  }

  private createDetailPromptAnswer(
    promptId: number,
    sequenceNumber: number,
    answer?: string
  ): FormGroup {
    return this.fb.group({
      behaviorStrategyDetailPromptId: [promptId],
      sequenceNumber: [sequenceNumber],
      answer: [
        answer ?? '',
        [
          Validators.required,
          Validators.maxLength(this.MAX_LENGTHS.detailPromptAnswer),
        ],
      ],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createDefaultGuideline(): FormGroup {
    const formGroup = this.createGuideline('');
    formGroup.get('sequenceNumber')?.setValue(1);
    return formGroup;
  }

  showAddButton(index: number): boolean {
    return index === this.guidelinesArray.length - 1;
  }

  showDeleteButton(index: number): boolean {
    return index > 0;
  }

  isLastGuideline(index: number): boolean {
    return index === this.guidelinesArray.length - 1;
  }

  showAddGuidelineOnDeleted(index: number): boolean {
    return this.isLastGuideline(index) && this.isGuidelineDeleted(index);
  }

  addGuideline(): void {
    const lastGuidelineIndex = this.guidelinesArray.length - 1;
    const lastGuidelineControl = this.guidelinesArray.at(lastGuidelineIndex);

    const isLastDeleted = this.isGuidelineDeleted(lastGuidelineIndex);
    if (
      !isLastDeleted &&
      !lastGuidelineControl.get('guideline')?.value?.trim()
    ) {
      return;
    }

    const newGuideline = this.createGuideline('');
    newGuideline
      .get('sequenceNumber')
      ?.setValue(this.guidelinesArray.length + 1);
    this.guidelinesArray.push(newGuideline);
    this.updateSequenceNumbers();
  }

  removeGuideline(index: number): void {
    this.guidelineToDeleteIndex = index;
    this.showDeleteConfirmModal = true;
  }

  confirmDeleteGuideline(): void {
    if (
      this.guidelineToDeleteIndex !== null &&
      this.showDeleteButton(this.guidelineToDeleteIndex)
    ) {
      this.deletedGuidelines.add(this.guidelineToDeleteIndex);
      this.showDeleteConfirmModal = false;
      this.guidelineToDeleteIndex = null;
    }
  }

  cancelDeleteGuideline(): void {
    this.showDeleteConfirmModal = false;
    this.guidelineToDeleteIndex = null;
  }

  undoGuideline(index: number): void {
    this.deletedGuidelines.delete(index);
  }

  isGuidelineDeleted(index: number): boolean {
    return this.deletedGuidelines.has(index);
  }

  private updateSequenceNumbers(): void {
    this.guidelinesArray.controls.forEach((control, index) => {
      control.get('sequenceNumber')?.setValue(index + 1);
    });
  }

  guidelinesRemainingChars(index: number): number {
    const control = this.guidelinesArray.at(index).get('guideline');
    const remaining = 500 - (control?.value?.length || 0);
    return Math.max(0, remaining);
  }

  saveStrategy(isPublish: boolean): void {
    this.isPublished = isPublish;
    if (this.mode === 'edit' || this.mode === 'guide') {
      this.createModification();
    } else {
      this.createOwnStrategy();
    }
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageOwnStrategyParams>(DRAWER_IDS.CREATE_OWN_STRATEGY)
      .pipe(
        takeUntil(this.destroy$),
        filter(params => !!params)
      )
      .subscribe(params => {
        if (params) {
          this.handleDrawerParams(params);
        }
      });
  }

  private handleDrawerParams(params: ManageOwnStrategyParams): void {
    this.caseParams = params.caseParams;
    this.strategy = params.strategy;
    this.behaviorId = params.behaviorId;
    this.mode = params?.mode || 'edit';
    this.strategyTypeId = params.strategyTypeId || 0;
    if (params.from) {
      this.from = params.from;
    }
    if ((this.mode === 'edit' || this.mode === 'guide') && this.strategy) {
      this.populateFormForEdit();
    }

    this.updateDetailsValidation();
  }

  private populateFormForEdit(): void {
    this.guidelinesArray.clear();
    this.detailPromptAnswersArray.clear();

    this.createOwnStrategyForm.patchValue({
      title: this.strategy?.title,
      description: this.strategy?.description,
      details: this.strategy?.details,
    });

    if (this.strategy?.guidelines && this.strategy.guidelines.length > 0) {
      this.strategy.guidelines.forEach(guideline => {
        this.guidelinesArray.push(
          this.createGuideline(guideline.guideline ?? '')
        );
      });
    } else {
      this.guidelinesArray.push(this.createGuideline(''));
    }

    const existingAnswersByPromptId = new Map(
      (this.strategy?.detailPromptAnswers || []).map(a => [
        a.behaviorStrategyDetailPromptId,
        a.answer,
      ])
    );

    this.strategy?.behaviorStrategyDetailPrompts?.forEach(prompt => {
      this.detailPromptAnswersArray.push(
        this.createDetailPromptAnswer(
          prompt.id,
          prompt.sequenceNumber,
          existingAnswersByPromptId.get(prompt.id) ?? ''
        )
      );
    });

    this.updateSequenceNumbers();
  }

  private updateDetailsValidation(): void {
    const detailsControl = this.createOwnStrategyForm.get('details');
    if (!detailsControl) {
      return;
    }

    const hasDetailPrompts =
      !!this.strategy?.behaviorStrategyDetailPrompts?.length;
    const shouldRequireDetails = this.mode === 'create' || !hasDetailPrompts;

    const validators = shouldRequireDetails
      ? [Validators.required, Validators.maxLength(this.MAX_LENGTHS.details)]
      : [Validators.maxLength(this.MAX_LENGTHS.details)];

    detailsControl.setValidators(validators);
    detailsControl.updateValueAndValidity({ emitEvent: false });
  }
  private getBasePayload() {
    const formValue = this.createOwnStrategyForm.value;

    const activeGuidelines = this.guidelinesArray.value.filter(
      (_: unknown, index: number) => !this.deletedGuidelines.has(index)
    );

    return {
      title: formValue.title ?? '',
      description: formValue.description ?? '',
      details: formValue.details ?? '',
      guidelines: activeGuidelines,
    };
  }

  private getModifiedFieldsForStrategy(isEdit: boolean): unknown[] {
    interface StrategyAuditValue {
      id?: number;
      title: string;
      description: string;
      details: string;
      guidelines?: string[];
      detailPromptAnswers?: BehaviorStrategyDetailPromptAnswerPayload[];
      [key: string]: unknown;
    }

    // Build oldValue and newValue with all relevant fields for audit
    const formValue = this.createOwnStrategyForm.value;
    const guidelines = (formValue.guidelines || []).map(
      (g: { guideline: string }) => g.guideline ?? ''
    );
    const detailPromptAnswers = this.getDetailPromptAnswersPayload();
    const newValue: StrategyAuditValue = {
      title: formValue.title ?? '',
      description: formValue.description ?? '',
      details: formValue.details ?? '',
    };
    if (isEdit && this.strategy) {
      // UPDATE: oldValue from this.strategy, newValue from form
      const oldGuidelines = (this.strategy.guidelines || []).map(
        g => g.guideline ?? ''
      );
      const oldValue: StrategyAuditValue = {
        id: this.strategy.id,
        title: this.strategy.title ?? '',
        description: this.strategy.description ?? '',
        details: this.strategy.details ?? '',
      };
      // Only include guidelines if changed
      const guidelinesChanged =
        JSON.stringify(guidelines) !== JSON.stringify(oldGuidelines);
      if (guidelinesChanged) {
        newValue.guidelines = guidelines;
        oldValue.guidelines = oldGuidelines;
      }

      const answerLookup = new Map(
        (this.strategy.detailPromptAnswers || []).map(a => [
          a.behaviorStrategyDetailPromptId,
          a.answer,
        ])
      );

      const oldDetailPromptAnswers: BehaviorStrategyDetailPromptAnswerPayload[] =
        (this.strategy.behaviorStrategyDetailPrompts || []).map(prompt => ({
          behaviorStrategyDetailPromptId: prompt.id,
          answer: answerLookup.get(prompt.id) ?? '',
          sequenceNumber: prompt.sequenceNumber,
        }));

      const detailPromptAnswersChanged =
        JSON.stringify(detailPromptAnswers) !==
        JSON.stringify(oldDetailPromptAnswers);

      if (detailPromptAnswersChanged) {
        newValue.detailPromptAnswers = detailPromptAnswers;
        oldValue.detailPromptAnswers = oldDetailPromptAnswers;
      }

      return this.auditContextService.buildFieldChanges(oldValue, newValue, {
        sensitiveFields: [],
        labelMap: {
          id: 'Strategy ID',
          title: 'Strategy Title',
          description: 'Description',
          details: 'Details',
          guidelines: 'Guidelines',
          detailPromptAnswers: 'Detail Prompt Answers',
        },
        excludeFields: ['id'],
      });
    } else {
      // CREATE: always include guidelines and isPublish
      newValue.guidelines = guidelines;
      newValue['isPublish'] = this.isPublished;
      return this.auditContextService.buildFieldChanges({}, newValue, {
        sensitiveFields: [],
        labelMap: {
          title: 'Strategy Title',
          description: 'Description',
          details: 'Details',
          guidelines: 'Guidelines',
          isPublish: 'District-level Publish',
        },
      });
    }
  }

  createOwnStrategy(): void {
    if (this.createOwnStrategyForm.invalid) return;

    const payload: CreateOwnStrategyPayload & { _modifiedFields: unknown[] } = {
      ...this.getBasePayload(),
      caseBehaviorId: this.behaviorId ?? 0,
      strategyTypeId: this.strategyTypeId ?? 0,
      isPublish: this.isPublished,
      _modifiedFields: this.getModifiedFieldsForStrategy(false),
    };

    this.submitStrategy(
      this.strategyService.createOwnStrategy(payload),
      'Behavior strategy added successfully'
    );
  }

  createModification(): void {
    if (this.createOwnStrategyForm.invalid) return;

    const payload = {
      ...this.getBasePayload(),
      detailPromptAnswers: this.getDetailPromptAnswersPayload(),
      caseBehaviorId: this.behaviorId ?? 0,
      behaviorStrategyId: this.strategy?.id ?? 0,
      modificationReason: 'Needs adjustment',
      _modifiedFields: this.getModifiedFieldsForStrategy(true),
    };

    this.submitStrategy(
      this.strategyService.createModificationStrategy(payload),
      'Behavior strategy updated successfully'
    );
  }

  private submitStrategy(
    request: Observable<unknown>,
    successMessage: string
  ): void {
    this.loaderService.show();

    request
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', successMessage);
          this.drawerService.closeDrawer(
            DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGY_SELECTION
          );
          this.closeDrawers();
          if (
            this.mode === 'edit' ||
            this.mode === 'create' ||
            this.mode === 'guide'
          ) {
            this.strategyService
              .getBehaviorStrategyList({
                caseBehaviorId: this.behaviorId ?? 0,
                strategyType: this.strategy?.behaviourStrategyTypeId,
                pageIndex: 1,
                pageSize: 10,
              })
              .pipe(finalize(() => this.loaderService.hide()))
              .subscribe();
            return;
          }

          this.router.navigate(['/insights'], {
            queryParams: { mode: 'view' },
          });
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  closeDrawers(): void {
    const drawer = this.drawerService.getDrawerRef(
      DRAWER_IDS.CREATE_OWN_STRATEGY
    );
    drawer?.close({ mode: this.getCloseMode() });
  }

  private getCloseMode(): 'view' | 'create' | 'guide' {
    return 'view';
  }

  getRemainingChars(controlName: string): number {
    const control = this.createOwnStrategyForm.get(controlName);
    if (!control) return 0;

    const currentLength = control.value?.length || 0;
    return currentLength;
  }

  getGuidelineRemainingChars(index: number): number {
    const control = this.guidelinesArray.at(index).get('guideline');
    if (!control) return this.MAX_LENGTHS.guideline;

    return Math.max(
      0,
      this.MAX_LENGTHS.guideline - (control.value?.length || 0)
    );
  }

  getDetailPromptAnswerCharsUsed(index: number): number {
    const control = this.detailPromptAnswersArray.at(index)?.get('answer');
    if (!control) return 0;

    return control.value?.length || 0;
  }

  isDetailPromptAnswerInvalid(index: number): boolean {
    const control = this.detailPromptAnswersArray.at(index)?.get('answer');
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getDetailPromptAnswerError(index: number): string {
    const control = this.detailPromptAnswersArray.at(index)?.get('answer');
    if (!control?.errors) {
      return '';
    }

    if (control.errors['required']) {
      return this.ERROR_MESSAGES.detailPromptAnswer.required;
    }

    if (control.errors['maxlength']) {
      return this.ERROR_MESSAGES.detailPromptAnswer.maxlength;
    }

    return 'Invalid input';
  }

  formatDetailPromptLabel(detailPrompt: string | null | undefined): string {
    const normalizedPrompt = (detailPrompt ?? '')
      .replaceAll(/\s+/g, ' ')
      .trim();

    if (!normalizedPrompt) {
      return '';
    }

    const firstAlphaIndex = normalizedPrompt.search(/[A-Za-z]/);

    if (firstAlphaIndex < 0) {
      return normalizedPrompt;
    }

    return (
      normalizedPrompt.slice(0, firstAlphaIndex) +
      normalizedPrompt.charAt(firstAlphaIndex).toUpperCase() +
      normalizedPrompt.slice(firstAlphaIndex + 1)
    );
  }

  private getDetailPromptAnswersPayload(): BehaviorStrategyDetailPromptAnswerPayload[] {
    if (this.mode !== 'edit' && this.mode !== 'guide') {
      return [];
    }

    return this.detailPromptAnswersArray.controls.map(control => ({
      behaviorStrategyDetailPromptId:
        control.get('behaviorStrategyDetailPromptId')?.value ?? 0,
      answer: (control.get('answer')?.value ?? '').trim(),
      sequenceNumber: control.get('sequenceNumber')?.value ?? 0,
    }));
  }

  isFieldInvalid(controlName: string): boolean {
    const control = this.createOwnStrategyForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }
}
